import { useEffect, useRef } from 'react';

interface HeroProps {
  onBookCall: () => void;
}

export default function Hero({ onBookCall }: HeroProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const PARTICLE_COUNT = 80;
    const CONNECTION_DISTANCE = 130;

    const particles: {
      x: number; y: number; vx: number; vy: number; r: number;
    }[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.5 + 0.5,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 240, 255, 0.3)';
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DISTANCE) {
            const alpha = (1 - dist / CONNECTION_DISTANCE) * 0.12;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 240, 255, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const trustLogos = [
    'FORBES', 'Ad Age', 'ADWEEK', 'Marketing Week', 'Inc. 500',
  ];

  return (
    <section id="home" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-black">
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      />

      {/* Radial glow center */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(0,240,255,0.04) 0%, transparent 70%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-6xl mx-auto w-full pt-28 pb-20">
        {/* Eyebrow */}
        <div className="mb-8 flex flex-col items-center gap-3 animate-[fadeInUp_0.6s_ease_0.1s_both]">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-electric-blue/30 bg-electric-blue/5">
            <span className="w-1.5 h-1.5 rounded-full bg-electric-blue animate-pulse-slow flex-shrink-0" />
            <span className="text-electric-blue text-xs font-semibold tracking-widest uppercase">
              The CGOeight System
            </span>
          </div>
          <span className="text-white/30 text-xs font-medium tracking-widest uppercase">
            AI-Powered Performance Marketing
          </span>
        </div>

        {/* Main headline */}
        <h1 className="font-display font-bold leading-[0.95] tracking-tight text-white mb-6 animate-[fadeInUp_0.7s_ease_0.2s_both]"
          style={{ fontSize: 'clamp(3.5rem, 8vw, 7.5rem)' }}
        >
          We <span className="text-electric-blue text-glow-blue">3X</span> Your<br />
          Profit in 30 Days.
        </h1>

        {/* Sub-headline */}
        <p className="text-white/55 font-sans font-normal max-w-2xl mb-4 animate-[fadeInUp_0.7s_ease_0.35s_both]"
          style={{ fontSize: 'clamp(1.05rem, 2vw, 1.3rem)', lineHeight: '1.65' }}
        >
          High-Performance Marketing for Music Festivals, Car Dealerships, Real Estate Agencies, &amp; Law Firms
        </p>

        <p className="text-white/30 text-sm md:text-base tracking-wide mb-10 animate-[fadeInUp_0.7s_ease_0.45s_both]">
          Millions of views. Millions of engagements. One algorithm that actually works.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-20 animate-[fadeInUp_0.7s_ease_0.55s_both]">
          <button
            onClick={onBookCall}
            className="whitespace-nowrap px-8 py-4 bg-electric-blue text-black text-base font-bold rounded-md glow-blue glow-blue-hover transition-all duration-300 tracking-wide cursor-pointer"
          >
            Get Your Free Profit Audit
          </button>
          <a
            href="#work"
            onClick={(e) => { e.preventDefault(); document.querySelector('#work')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="whitespace-nowrap flex items-center gap-2 px-6 py-4 text-white/70 hover:text-white text-base font-medium border border-white/10 rounded-md hover:border-white/25 transition-all duration-300 cursor-pointer"
          >
            Watch 60-second case study
            <i className="ri-arrow-right-line text-electric-blue" />
          </a>
        </div>


      </div>

      {/* Bottom scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-pulse-slow">
        <span className="text-white/20 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/20 to-transparent" />
      </div>
    </section>
  );
}
